import { motion, AnimatePresence } from "motion/react";
import { auctionItems } from "@/mocks/auctionItems";

interface ItemPickerProps {
  selectedIds: string[];
  onAdd: (id: string) => void;
  onRemove: (id: string) => void;
  maxItems: number;
  show: boolean;
  onToggle: () => void;
}

const gradeColor: Record<string, string> = {
  A: "bg-emerald-500",
  B: "bg-amber-500",
  C: "bg-gray-400",
};

function fmtShort(n: number): string {
  if (n >= 100000000) return (n / 100000000).toFixed(1) + "억";
  if (n >= 10000) return Math.round(n / 10000) + "만";
  return n.toLocaleString();
}

export default function ItemPicker({ selectedIds, onAdd, onRemove, maxItems, show, onToggle }: ItemPickerProps) {
  const isFull = selectedIds.length >= maxItems;

  return (
    <div className="mb-6">
      {/* 토글 헤더 */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-3.5 bg-white border border-[#E8E5E0] rounded-xl hover:border-[#C4956A]/50 transition-all cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#C4956A]/10 flex items-center justify-center">
            <i className="ri-add-circle-line text-[#C4956A]"></i>
          </div>
          <div className="text-left">
            <p className="text-sm font-bold text-[#1C1917]">비교 물건 선택</p>
            <p className="text-xs text-[#8C8780]">
              {selectedIds.length > 0
                ? `${selectedIds.length}개 선택됨 (최대 ${maxItems}개)`
                : `최대 ${maxItems}개까지 선택하여 비교할 수 있습니다`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.length > 0 && (
            <span className="px-2.5 py-1 bg-[#C4956A] text-white text-xs font-bold rounded-full">
              {selectedIds.length}/{maxItems}
            </span>
          )}
          {show
            ? <i className="ri-arrow-up-s-line text-[#8C8780]"></i>
            : <i className="ri-arrow-down-s-line text-[#8C8780]"></i>
          }
        </div>
      </button>

      <AnimatePresence>
        {show && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] as const }}
            className="overflow-hidden"
          >
            <div className="mt-3 bg-white border border-[#E8E5E0] rounded-xl p-5">
              {isFull && (
                <div className="mb-4 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-xs text-amber-700">
                  <i className="ri-information-line"></i>
                  최대 {maxItems}개까지 비교할 수 있습니다. 물건을 제거한 후 새로 추가하세요.
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {auctionItems.map((item) => {
                  const isSelected = selectedIds.includes(item.id);
                  const isDisabled = isFull && !isSelected;
                  return (
                    <motion.div
                      key={item.id}
                      whileHover={!isDisabled ? { y: -2 } : {}}
                      className={`relative rounded-xl border-2 p-3.5 transition-all ${
                        isSelected
                          ? "border-[#C4956A] bg-[#C4956A]/5"
                          : isDisabled
                          ? "border-[#F0EDE8] bg-[#FAFAF8] opacity-50"
                          : "border-[#E8E5E0] bg-white hover:border-[#C4956A]/50 cursor-pointer"
                      }`}
                      onClick={() => {
                        if (isDisabled) return;
                        if (isSelected) onRemove(item.id);
                        else onAdd(item.id);
                      }}
                    >
                      {/* 선택 체크 */}
                      <div className={`absolute top-3 right-3 w-5 h-5 rounded-full flex items-center justify-center transition-all ${
                        isSelected ? "bg-[#C4956A]" : "bg-[#F0EDE8]"
                      }`}>
                        {isSelected
                          ? <i className="ri-check-line text-white text-xs"></i>
                          : <i className="ri-add-line text-[#B8B3AE] text-xs"></i>
                        }
                      </div>

                      {/* 등급 배지 */}
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-0.5 text-[10px] font-bold text-white rounded-full ${gradeColor[item.grade] ?? "bg-gray-400"}`}>
                          {item.grade}등급
                        </span>
                        <span className="text-[10px] text-[#8C8780]">유찰 {item.failureCount}회</span>
                        {item.hasTenant && (
                          <span className="text-[10px] text-rose-500">임차인 있음</span>
                        )}
                      </div>

                      {/* 물건 정보 */}
                      <p className="text-xs font-bold text-[#1C1917] mb-0.5 pr-6 line-clamp-1">{item.location}</p>
                      <p className="text-[10px] text-[#8C8780] mb-2">{item.propertyType} · {item.court}</p>

                      {/* 가격 */}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-[10px] text-[#B8B3AE]">감정가</p>
                          <p className="text-xs font-semibold text-[#5C5650]">{fmtShort(item.appraisedValue)}원</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[10px] text-[#B8B3AE]">최저가</p>
                          <p className="text-xs font-bold text-[#C4956A]">{fmtShort(item.minimumPrice)}원</p>
                        </div>
                      </div>

                      {/* 입찰일 */}
                      <div className="mt-2 pt-2 border-t border-[#F0EDE8] flex items-center justify-between">
                        <span className="text-[10px] text-[#B8B3AE]">{item.auctionDate}</span>
                        <span className="text-[10px] font-medium text-emerald-600">
                          예상수익 {fmtShort(item.expectedProfit)}원
                        </span>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}